import { useContext, useMemo } from 'react';

import { PermissionContext } from '@vyce/core/src/contexts/permissionContext';
import { TabItem } from '@vyce/core/src/types/common';
import {
  getResourceIdsWithPermission,
  hasAnyCompanyPermission,
  hasCompanyPermission,
  hasPermissionAnywhere,
  hasPermissionOnAnyResource,
  hasResourcePermission,
  RESOURCE_TYPES,
} from '@vyce/core/src/utils/permissions';

/**
 * Read the current user's rights. Fail-closed: before the position has loaded
 * every predicate answers `false`, so a screen must render its loader on
 * `!ready` rather than treating it as a denial.
 */
export const usePermissions = () => {
  const { position, isInternalAdmin, ready } = useContext(PermissionContext);

  return useMemo(() => {
    const can = (permission: string) =>
      isInternalAdmin || (ready && hasCompanyPermission(position, permission));

    const canAny = (permissions: string[]) =>
      isInternalAdmin || (ready && hasAnyCompanyPermission(position, permissions));

    const canOnResource = (permission: string, resourceType: string, resourceId?: string) =>
      isInternalAdmin || (ready && hasResourcePermission(position, permission, resourceType, resourceId));

    const canOnAnyResource = (permission: string, resourceType: string) =>
      isInternalAdmin || (ready && hasPermissionOnAnyResource(position, permission, resourceType));

    /**
     * Company-wide or on any single resource. Use it for list-shaped things — a
     * screen, a tab, a search request the backend filters itself — where a
     * manager scoped to one location must still get through.
     */
    const canAnywhere = (permission: string) =>
      isInternalAdmin || (ready && hasPermissionAnywhere(position, permission));

    /** `null` means "every resource" — an owner or a Legend admin. */
    const resourceIdsWith = (permission: string, resourceType: string): string[] | null => {
      if (isInternalAdmin) return null;
      if (!ready) return [];
      return getResourceIdsWithPermission(position, permission, resourceType);
    };

    /**
     * A tab must stay reachable when the permission behind it is held on a
     * single resource — a manager scoped to one location still needs the Time
     * tabs. So a tab counts as visible if the right is held company-wide OR on
     * any resource at all; the screen behind it does the per-row gating.
     */
    const filterTabs = <T extends TabItem>(tabs: T[]): T[] => {
      if (isInternalAdmin) return tabs;
      if (!ready) return tabs.filter(tab => !tab.permission);
      return tabs.filter(tab => {
        if (!tab.permission) return true;
        const required = Array.isArray(tab.permission) ? tab.permission : [tab.permission];
        return (
          hasAnyCompanyPermission(position, required) ||
          required.some(permission =>
            RESOURCE_TYPES.some(resourceType =>
              hasPermissionOnAnyResource(position, permission, resourceType)
            )
          )
        );
      });
    };

    return {
      can,
      canAny,
      canOnResource,
      canOnAnyResource,
      canAnywhere,
      resourceIdsWith,
      filterTabs,
      isOwner: !!position?.owner,
      isInternalAdmin,
      ready,
    };
  }, [position, isInternalAdmin, ready]);
};
